import {
  applyPigmentAuthoringGuidedEdit,
  createPigmentAuthoringContext,
  instantiatePigmentAuthoringBlock,
  parsePigmentAuthoringPropertyValue,
  pigmentAuthoringPlacementOptions,
  pigmentAuthoringPropertyOptions,
  pigmentAuthoringValueSource,
  PigmentRefusal,
  isPigmentRevisionMismatch,
  type PigmentAuthoringBlockContext,
  type PigmentAuthoringContext,
  type PigmentAuthoringPlacementOption,
  type PigmentAuthoringSnapshot,
} from "@pigment/client";
import {
  isPigmentDocument,
  type PigmentDocument,
  type PigmentDocumentOperation,
  type PigmentDocumentMutationDiff,
  type PigmentDocumentReconcileResult,
} from "@pigment/core";

export type OperationDraft = {
  op: PigmentDocumentOperation["op"];
  id: string;
  key: string;
  value: string;
  blockType: string;
  exampleIndex: number;
  placementId: string;
};

export const EMPTY_OPERATION: OperationDraft = {
  op: "set",
  id: "",
  key: "",
  value: "",
  blockType: "",
  exampleIndex: 0,
  placementId: "",
};

export type RefusalPresentation = {
  status: number | null;
  eyebrow: string;
  title: string;
  detail: string;
  recovery: string;
  body?: unknown;
};

/**
 * Resolve a user-entered host only at the explicit Connect boundary.
 *
 * The field itself is deliberately allowed to contain an incomplete URL while
 * someone types. Constructing a client from each keystroke turns ordinary
 * editing into a render-time exception and can blank the entire console.
 */
export function normalizePigmentHostOrigin(source: string): string {
  const value = source.trim();
  if (!value) throw new Error("Enter a Pigment host origin before connecting.");
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("Pigment host must be an absolute http(s) origin.");
  }
  if (url.username || url.password || url.search || url.hash || (url.pathname !== "/" && url.pathname !== "")) {
    throw new Error("Pigment host must be an origin without credentials, path, query, or fragment.");
  }
  const localHttp = url.protocol === "http:"
    && (url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]");
  if (url.protocol !== "https:" && !localHttp) {
    throw new Error("Pigment hosts require HTTPS; plain HTTP is allowed only for local development.");
  }
  return url.origin;
}

/** Initialize an editor from the exact host-verified authoring base. */
export function desiredDocumentSource(snapshot: Pick<PigmentAuthoringSnapshot, "authoringBase">): string {
  if (!snapshot.authoringBase) {
    throw new Error("This page has no durable draft or explicit published/seed authoring base. The host must provide one before desired-state authoring can begin.");
  }
  return JSON.stringify(snapshot.authoringBase.document, null, 2);
}

/** Parse only a complete semantic document; mutations are never accepted here. */
export function desiredDocumentFromJson(source: string): PigmentDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch (error) {
    throw new Error(`Desired document JSON is not valid: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!isPigmentDocument(parsed)) {
    throw new Error("Expected a complete Pigment document. Edit desired semantic state, not a mutation or partial patch.");
  }
  return parsed;
}

/**
 * Apply one guided edit directly to local desired state through core's atomic
 * executor. The UI never accumulates or exposes a second mutation program.
 */
export function applyGuidedOperation(
  source: string,
  draft: OperationDraft,
  context: PigmentAuthoringContext,
  placements: PigmentAuthoringPlacementOption[] = guidedPlacementOptions(context, source, draft),
): string {
  const document = desiredDocumentFromJson(source);
  const id = required(draft.id, "Block ID");
  if (draft.op === "remove") {
    return JSON.stringify(applyPigmentAuthoringGuidedEdit(context, document, { op: "remove", id }), null, 2);
  }
  if (draft.op === "reset") {
    return JSON.stringify(applyPigmentAuthoringGuidedEdit(context, document, { op: "reset", id, key: required(draft.key, "Property") }), null, 2);
  }
  if (draft.op === "set") {
    const property = pigmentAuthoringPropertyOptions(selectedAuthoringBlock(context, id))
      .find((candidate) => candidate.key === draft.key);
    if (!property) throw new Error("Choose a declared or currently authored property.");
    const value = parsePigmentAuthoringPropertyValue(draft.value, property.schema);
    return JSON.stringify(applyPigmentAuthoringGuidedEdit(context, document, { op: "set", id, key: property.key, value }), null, 2);
  }
  const placement = placements.find((candidate) =>
    candidate.selectionKey === draft.placementId
    || (candidate.mode !== "indexed" && candidate.id === draft.placementId)
  );
  if (!placement) throw new Error("Choose a legal placement from the current host contract.");
  if (draft.op === "move") {
    return JSON.stringify(applyPigmentAuthoringGuidedEdit(context, document, { op: "move", id, placement }), null, 2);
  }
  const capability = context.availableBlocks.find((candidate) => candidate.type === draft.blockType);
  const block = instantiatePigmentAuthoringBlock(capability, id, draft.exampleIndex);
  return JSON.stringify(applyPigmentAuthoringGuidedEdit(context, document, { op: "insert", block, placement }), null, 2);
}

export function desiredAuthoringContext(
  snapshot: PigmentAuthoringSnapshot | null,
  source: string,
): PigmentAuthoringContext | null {
  if (!snapshot) return null;
  try {
    return createPigmentAuthoringContext(snapshot.context.manifest, desiredDocumentFromJson(source));
  } catch {
    // Advanced JSON may be temporarily malformed while someone types. Keep the
    // editor available, but withhold guided edits until it is a full document.
    return null;
  }
}

export function selectedAuthoringBlock(context: PigmentAuthoringContext | null, id: string): PigmentAuthoringBlockContext | null {
  return context?.blocks.find((block) => block.id === id) ?? null;
}

export function selectAuthoringBlock(draft: OperationDraft, context: PigmentAuthoringContext | null, id: string): OperationDraft {
  const options = pigmentAuthoringPropertyOptions(selectedAuthoringBlock(context, id));
  const key = options.some((option) => option.key === draft.key) ? draft.key : (options[0]?.key ?? "");
  return selectAuthoringProperty({ ...draft, id, placementId: "" }, context, key);
}

export function selectAuthoringProperty(draft: OperationDraft, context: PigmentAuthoringContext | null, key: string): OperationDraft {
  const option = pigmentAuthoringPropertyOptions(selectedAuthoringBlock(context, draft.id)).find((property) => property.key === key);
  if (!option) return { ...draft, key };
  const selected = option.hasCurrent ? option.current : option.hasDefault ? option.defaultValue : undefined;
  const value = selected === undefined ? "" : pigmentAuthoringValueSource(selected, option.schema);
  return { ...draft, key, value };
}

export function guidedPlacementOptions(
  context: PigmentAuthoringContext | null,
  source: string,
  draft: OperationDraft,
): PigmentAuthoringPlacementOption[] {
  if (!context || (draft.op !== "insert" && draft.op !== "move")) return [];
  let document: PigmentDocument;
  try {
    document = desiredDocumentFromJson(source);
  } catch {
    return [];
  }
  const blockType = draft.op === "insert"
    ? draft.blockType
    : selectedAuthoringBlock(context, draft.id)?.type ?? "";
  if (!blockType) return [];
  return pigmentAuthoringPlacementOptions({
    context,
    document,
    blockType,
    ...(draft.op === "move" ? { movingBlockId: draft.id } : {}),
  });
}

export function presentReconciliationRefusal(
  result: Extract<PigmentDocumentReconcileResult, { ok: false }>,
): RefusalPresentation {
  return {
    status: null,
    eyebrow: "Desired state refused",
    title: "Pigment could not represent this document safely",
    detail: result.diagnostics.map((diagnostic) => `${diagnostic.path}: ${diagnostic.message}`).join(" · "),
    recovery: "Repair the desired document or its stable identities, then reconcile again. No plan, preview, or mutation authority was created.",
    body: result,
  };
}

export function describeMutationDiff(diff: PigmentDocumentMutationDiff): string {
  if (diff.op === "set-theme") return `theme: ${diff.hadPrevious ? "replace" : "create"}`;
  if (diff.op === "reset-theme") return "theme: reset";
  if (diff.op === "set-chrome-mode") return `chrome.${diff.slot}: ${diff.previousMode ?? "unset"} → ${diff.mode}`;
  if (diff.op === "set") return `${diff.id}.${diff.key}: ${diff.hadPrevious ? "replace" : "create"}`;
  if (diff.op === "reset") return `${diff.id}.${diff.key}: reset`;
  if (diff.op === "insert") return `${diff.id}: insert at ${describePlacement(diff.to)}`;
  if (diff.op === "remove") return `${diff.id}: remove from ${describePlacement(diff.from)}`;
  return `${diff.id}: move ${describePlacement(diff.from)} → ${describePlacement(diff.to)}`;
}

function describePlacement(placement: { root: { kind: "blocks" } | { kind: "chrome"; slot: string }; parentId: string | null; index: number }): string {
  const root = placement.root.kind === "blocks" ? "page" : `chrome.${placement.root.slot}`;
  return `${root}/${placement.parentId ?? "root"}[${placement.index}]`;
}

function required(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is required.`);
  return trimmed;
}

export function presentRefusal(error: unknown): RefusalPresentation {
  if (!(error instanceof PigmentRefusal)) {
    return {
      status: null,
      eyebrow: "Console error",
      title: "The workflow stopped locally",
      detail: error instanceof Error ? error.message : String(error),
      recovery: "Correct the desired document or connection and retry the same explicit step.",
    };
  }
  if (error.status === 403) {
    return {
      status: 403,
      eyebrow: "Authority refused · 403",
      title: "This session cannot perform that action",
      detail: error.message,
      recovery: "Sign in with the delegated user authority this host expects, or ask an administrator for the missing grant.",
      body: error.body,
    };
  }
  if (error.status === 409) {
    const revisions = isPigmentRevisionMismatch(error)
      ? ` Expected revision ${error.body.conflict.expectedRevision}; the host is at ${error.body.conflict.actualRevision}.`
      : "";
    return {
      status: 409,
      eyebrow: "Lineage conflict · 409",
      title: "The draft changed after this work began",
      detail: `${error.message}${revisions}`,
      recovery: "Read the verified base again, review the complete desired document, then reconcile and inspect a fresh candidate. The stale mutation was not retried.",
      body: error.body,
    };
  }
  if (error.status === 422) {
    const diagnostics = error.body.validation?.diagnostics ?? [];
    return {
      status: 422,
      eyebrow: "Validation refused · 422",
      title: "The candidate is not publishable",
      detail: diagnostics.length
        ? diagnostics.map((item) => `${item.path}: ${item.message}`).join(" · ")
        : error.message,
      recovery: "Repair the reported semantic or conformance issues, then reconcile and create a new plan and preview.",
      body: error.body,
    };
  }
  return {
    status: error.status,
    eyebrow: `Host refused · ${error.status}`,
    title: "The host did not accept this step",
    detail: error.message,
    recovery: "Inspect the typed response below before deciding whether to retry.",
    body: error.body,
  };
}
